import { useState } from "react";
import { X, BadgeCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ClaimDialog({ profileId, username, onClose, onClaimed }) {
  const [statement, setStatement] = useState("");
  const [a1, setA1] = useState(false);
  const [a2, setA2] = useState(false);
  const [a3, setA3] = useState(false);
  const [busy, setBusy] = useState(false);

  const ok = statement.trim().length >= 20 && a1 && a2 && a3;

  const submit = async () => {
    setBusy(true);
    try {
      await api.claimProfile(profileId, {
        statement: statement.trim(),
        attest_is_me: a1, attest_honest_use: a2, attest_false_claim_consequence: a3,
      });
      toast.success("Claim submitted — self-attested status active.");
      onClaimed?.();
      onClose?.();
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(typeof det === "object" ? det.message : (det || "Claim failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="claim-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-[#FF1A82]/40 glow-lime p-7 animate-fade-up">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#5A5A5A] hover:text-[#1A1A1A]" data-testid="claim-close">
          <X className="w-5 h-5" />
        </button>
        <div className="text-xs uppercase tracking-[0.32em] text-[#FF1A82] mb-2 inline-flex items-center gap-2">
          <BadgeCheck className="w-4 h-4" /> Claim verification
        </div>
        <h2 className="font-heading text-3xl text-[#1A1A1A]">This is <span className="italic text-[#10B981]">{username}</span>?</h2>
        <p className="text-[#3A3A3A] text-sm mt-2">Tell us briefly why this profile is you. AI moderation reads this. Your claim will be marked <span className="text-[#FF1A82]">Self-attested</span> until further verification.</p>

        <textarea
          data-testid="claim-statement"
          value={statement}
          onChange={e => setStatement(e.target.value)}
          maxLength={300}
          rows={3}
          placeholder="e.g. This is my Hinge profile — I noticed a review notification and want to share my side."
          className="mt-4 w-full bg-black/5 border border-black/15 rounded-2xl px-4 py-3 text-[#1A1A1A] outline-none focus:border-[#FF1A82] resize-none"
        />
        <div className="text-xs text-[#5A5A5A] text-right mt-1">{statement.length}/300 · min 20</div>

        <div className="mt-5 space-y-3 text-sm">
          <Check label="I am the person reviewed on this profile." checked={a1} onChange={setA1} testid="attest-is-me" />
          <Check label="I will use this profile honestly and respectfully." checked={a2} onChange={setA2} testid="attest-honest-use" />
          <Check label="I understand that false claims will result in an account restriction." checked={a3} onChange={setA3} testid="attest-false-claim" />
        </div>

        <div className="mt-5 rounded-2xl bg-[#10B981]/10 border border-[#10B981]/30 p-4 text-xs text-[#3A3A3A] flex gap-2">
          <AlertTriangle className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
          <span>The original reviewer and the community can <span className="text-[#10B981]">dispute</span> a self-attested claim. Disputes revert the profile to unclaimed for everyone's safety.</span>
        </div>

        <button onClick={submit} disabled={!ok || busy} data-testid="claim-submit"
          className="mt-6 w-full px-6 py-3 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition disabled:opacity-40 disabled:shadow-none">
          {busy ? "Submitting…" : "Submit claim"}
        </button>
      </div>
    </div>
  );
}

function Check({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-1 w-5 h-5 accent-neon-lime" data-testid={testid} />
      <span className="text-[#1A1A1A]">{label}</span>
    </label>
  );
}
